// Global Instructions Rule Applied!

import React, { useMemo, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../../core/utils/errorToast.tsx'
import { KanbanBoard } from '../../../../../../core/components/KanbanBoard/index.tsx'
import { KanbanColumn, ColumnHeaderConfig } from '../../../../../../core/components/KanbanBoard/interfaces.ts'
import { useDealManager } from '../../../../hooks/useDealManager.ts'
import { updateDeal } from '../../../../services/dealsService.ts'
import { useDealPipelineData } from '../../../../hooks/useDealPipelineData.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../../../../core/utils/endpoints.ts'
import { useCurrencies } from '../../../../../currencies/useCurrencies.ts'
import { useTabManagerContext } from '../../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useDealPipelineDrawer } from '../../../../hooks/useDealPipelineDrawer.ts'
import { useCommitDealGate } from '../../../../hooks/useCommitDealGate.tsx'
import { useDispatch } from 'react-redux'
import { updateDealRecord } from '../../../../../../state_manager/state_update_actions/deals/index.ts'
import { DealFields, DealRecord } from '../../../../../../state_manager/data_models/deal_record.ts'
import { planColumnMove } from '../../../../../../core/components/KanbanBoard/controller/parts/priorityUtils.ts'
import { sortItemsByPriority } from '../../../../../../core/components/KanbanBoard/controller/parts/columnUtils.ts'
import { TABLE_DEALS, TABLE_PIPELINE_DEALS, VIEW_TYPE_KANBAN_BOARD } from '../../../../../../state_manager/globals.ts'
import { MESSAGE_UPDATE_SUCCESSFUL } from '../../../../../../state_manager/constants/strings.ts'
import { DealKanbanItem } from './interfaces.ts'
import { PipelineStageEntry } from '../../../../../../state_manager/data_models/pipeline_stage_entry.ts'
import { PipelineStageEntryFields } from '../../../../../../state_manager/data_models/pipeline_stage_entry.ts'
import { useCapabilityCheck } from '../../../../../../core/hooks/useCapability.ts'
import { CAPABILITY_PIPELINE_STAGE_MOVE } from '../../../../../../core/hooks/useStageMoveRights.ts'
import { getAccessToken } from '../../../../../../core/auth/authService'
import { store } from '../../../../../../state_manager/store.ts'

import Button from '../../../../../../core/components/Button.jsx'
import ViewToolbar from '../../../../../../core/components/ViewToolbar/index.tsx'
import { useEarlyStageCardConfig } from '../../../../../../core/components/KanbanBoard/components/cards/EarlyStage.tsx'
import { VIEW_TASK_CONFIG } from '../../../../../../ui_configs/fund_management/view_task_config.ts'

const EarlyStageKanban: React.FC = () => {
  const dispatch = useDispatch()
  const { gate: commitGate, holder: commitGateHolder } = useCommitDealGate()
  const { openFormInTab } = useTabManagerContext()
  const [successfullyMovedCardId, setSuccessfullyMovedCardId] = useState<string | null>(null)

  // Pipeline data hook
  const {
    pipelineDealsData: deals,
    pipelineDealsLoading: dealsLoading,
    handleFetchPipelineDeals: handleRefreshPipeline
  } = useDealPipelineData()

  // Deal manager hook
  const { defaultPipelineId, getPipelineStages } = useDealManager()
  const pipelineStages: PipelineStageEntry[] = useMemo(
    () => getPipelineStages(defaultPipelineId || '', VIEW_TYPE_KANBAN_BOARD),
    [getPipelineStages, defaultPipelineId]
  )

  // Get pipeline configs and stages from Redux state for label lookup
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: unknown[] } }) => state.globalConfigs?.pipeline_configs || []
  )
  const allPipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: unknown[] } }) => state.globalConfigs?.pipeline_stages || []
  )

  // Currencies hook for formatting
  const { formatAmountById, getCurrencyById } = useCurrencies()

  // Drawer management hook
  const { handleOpenDrawerForDeal, handleTasksClick, handleTaskToggle } = useDealPipelineDrawer(deals)

  // Who may move deals into or out of each stage (pipeline_stage.move, a rule
  // per pipeline_config row): a column the user may not move into or out of is
  // read-only for them - no drop in, no drag out - with the reason on its marker.
  // This replaced the board-only drop_option_kb switch.
  const canMoveStage = useCapabilityCheck()

  // Transform pipeline stages to KanbanColumn format
  const columns: KanbanColumn[] = useMemo(() => {
    if (!pipelineStages.length) return []

    return pipelineStages.map((stage) => {
      const id = stage[`${PipelineStageEntryFields.id}`] as string
      const name = (stage[`${PipelineStageEntryFields.prog__name}`] as string) || ''
      const move = canMoveStage(CAPABILITY_PIPELINE_STAGE_MOVE, id, name)
      return {
        id,
        name,
        sequence: (stage[`${PipelineStageEntryFields.sequence_kb}`] as number | null | undefined) ?? null,
        visible: stage[`${PipelineStageEntryFields.visible_kb}`] as boolean,
        dropOption: move.allowed,
        readOnlyReason: move.reason,
        colour: (stage[PipelineStageEntryFields.colour] as string | null | undefined) || undefined
      }
    })
  }, [pipelineStages, canMoveStage])

  // Transform deals to KanbanItem format
  const kanbanItems: DealKanbanItem[] = useMemo(() => {
    if (!deals || !deals.length) return []

    return deals.map((deal: DealRecord) => {
      const dealId = deal[DealFields.deals__id] || ''
      return {
        ...deal,
        id: dealId,
        columnId: deal[DealFields.pipeline_config__id] || '',
        [DealFields.prog__tasks]: deal[DealFields.prog__tasks] || []
      }
    })
  }, [deals])

  // Sort items by priority within each column
  const sortedItems: DealKanbanItem[] = useMemo(() => {
    const itemsByColumn = new Map<string, DealKanbanItem[]>()

    kanbanItems.forEach((item) => {
      const columnId = item.columnId || ''
      if (!itemsByColumn.has(columnId)) {
        itemsByColumn.set(columnId, [])
      }
      itemsByColumn.get(columnId)?.push(item)
    })

    const sorted: DealKanbanItem[] = []
    columns.forEach((column) => {
      const columnItems = itemsByColumn.get(column.id) || []
      const sortedColumnItems = sortItemsByPriority(
        columnItems,
        (item) => item[DealFields.pipeline_deals__sequence] as number | null | undefined
      )
      sorted.push(...sortedColumnItems)
    })

    return sorted
  }, [kanbanItems, columns])

  // Handle deal field updates (for inline editing)
  const handleDealUpdate = useCallback(
    async (dealId: string, fieldName: string, fieldValue: unknown): Promise<void> => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        showErrorToast('Authentication required')
        return
      }

      try {
        const response = await updateDeal(dealId, { [fieldName]: fieldValue }, currentToken)

        if (response.error) {
          showErrorToast(response.error)
        } else {
          // Update Redux store with the updated deal
          if (response.data) {
            dispatch(updateDealRecord({ dealId, updates: response.data }))
          }
          handleRefreshPipeline()
        }
      } catch (error) {
        showErrorToast('Failed to update deal')
        console.error('Error updating deal:', error)
      }
    },
    [handleRefreshPipeline, dispatch]
  )

  // Handle move deal between columns
  const handleMoveDeal = useCallback(
    async (dealId: string, fromColumnId: string, toColumnId: string, newIndex: number): Promise<void> => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) return

      try {
        const movedItem = sortedItems.find((item) => item.id === dealId)
        if (!movedItem) return

        // Commit gate: a drop into an origination Committed column with details
        // missing prompts first; cancelling leaves the card where it was.
        if (fromColumnId !== toColumnId) {
          const proceed = await commitGate(movedItem, toColumnId)
          if (!proceed) return
        }

        // Plan against the target column as displayed: for a same-column drag
        // the moved card is still in it and the DropZone index counts it.
        const targetColumnItems = sortedItems.filter((item) => item.columnId === toColumnId)
        const plan = planColumnMove(
          targetColumnItems,
          movedItem,
          newIndex,
          (item) => item[DealFields.pipeline_deals__sequence]
        )

        // Dropped into its own slot — nothing changes
        if (plan.sequence === null) return
        const newSequence = plan.sequence

        // Get pipeline_deals ID from the deal record
        const pipelineDealsId = movedItem[DealFields.pipeline_deals__id]
        if (!pipelineDealsId) {
          showErrorToast('Pipeline deals record not found for this deal')
          return
        }

        // Prepare updates - transform field names to API format for pipeline_deals
        const apiUpdates: Record<string, unknown> = {
          id: pipelineDealsId,
          sequence: newSequence
        }

        // Prepare Redux updates
        const reduxUpdates: Record<string, unknown> = {
          [DealFields.pipeline_deals__sequence]: newSequence
        }

        // Only update stage if moving between columns
        if (fromColumnId !== toColumnId) {
          // toColumnId is the pipeline_config ID, which is what we use for the stage field
          apiUpdates.stage = toColumnId
          apiUpdates.stage_date = new Date().toISOString().split('T')[0]

          // Add to Redux updates
          reduxUpdates[DealFields.pipeline_config__id] = toColumnId
          reduxUpdates[DealFields.pipeline_deals__stage] = toColumnId
          reduxUpdates[DealFields.pipeline_deals__stage_date] = apiUpdates.stage_date

          // Calculate and update pipeline stage label
          const config = (pipelineConfigs as Array<{ id?: string; stage?: string }>).find((c) => c.id === toColumnId)
          if (config) {
            const stage = (allPipelineStages as Array<{ id?: string; name?: string | null }>).find(
              (s) => s.id === config.stage
            )
            if (stage && stage.name) {
              reduxUpdates[DealFields.pipeline_deals__stage__label] = stage.name
            }
          }
        }

        // Make PATCH request to /pipeline-deals endpoint
        const baseUrl = getBackendBaseUrl()
        const url = buildFullUrl(baseUrl, API_ROUTES.PIPELINE_DEALS.PATCH)
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        }

        if (currentToken) {
          headers['Authorization'] = `Bearer ${currentToken}`
        }

        const response = await fetch(url, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(apiUpdates)
        })

        if (!response.ok) {
          let errorMessage = `Failed to move deal: ${response.status} ${response.statusText}`
          try {
            const errorData = await response.json()
            if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.error) {
              errorMessage = errorData.error
            }
          } catch {
            // If we can't parse error response, use the default message
          }
          showErrorToast(errorMessage)
        } else {
          // Neighbours renumbered so the column order is unambiguous after the drop
          const renumberResponses = await Promise.all(
            plan.renumbered.map(({ item, sequence }) =>
              fetch(url, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ id: item[DealFields.pipeline_deals__id], sequence })
              })
            )
          )
          if (renumberResponses.some((r) => !r.ok)) {
            showErrorToast('Failed to renumber the column — reloading the pipeline')
            handleRefreshPipeline()
            return
          }

          // Update Redux store locally instead of refetching everything
          dispatch(updateDealRecord({ dealId, updates: reduxUpdates }))
          plan.renumbered.forEach(({ item, sequence }) => {
            dispatch(
              updateDealRecord({ dealId: item.id, updates: { [DealFields.pipeline_deals__sequence]: sequence } })
            )
          })
          toast.success(MESSAGE_UPDATE_SUCCESSFUL)

          // Trigger pulsing animation on the moved card
          setSuccessfullyMovedCardId(dealId)
          // Clear after animation completes (2 pulses * ~0.6s each = ~1.2s)
          setTimeout(() => {
            setSuccessfullyMovedCardId(null)
          }, 1200)
        }
      } catch (error) {
        showErrorToast('Failed to move deal')
        console.error('Error moving deal:', error)
      }
    },
    [sortedItems, dispatch, pipelineConfigs, allPipelineStages, handleRefreshPipeline, commitGate]
  )

  // Handle patch success for InputManager
  const handlePatchSuccess = useCallback(
    (recordId: string, updates: Record<string, unknown>): void => {
      dispatch(updateDealRecord({ dealId: recordId, updates }))
    },
    [dispatch]
  )

  // Handler for company name hyperlink clicks
  const handleCompanyNameClick = useCallback(
    (e: React.MouseEvent, companyId: string, companyName: string) => {
      e.stopPropagation() // Prevent card click events
      if (companyId) {
        openFormInTab('companies', companyId, companyName)
      }
    },
    [openFormInTab]
  )

  // Handler for deal name hyperlink clicks — opens the full deal form
  const handleDealNameClick = useCallback(
    (e: React.MouseEvent, dealId: string, dealName: string) => {
      e.stopPropagation() // Prevent card click events
      if (dealId) {
        openFormInTab('deals', dealId, dealName)
      }
    },
    [openFormInTab]
  )

  // Card configuration
  const cardConfig = useEarlyStageCardConfig({
    handleOpenDrawerForDeal,
    handleTasksClick,
    handleTaskToggle,
    getCurrencyById,
    handlePatchSuccess,
    handleCompanyNameClick,
    handleDealNameClick,
    taskConfig: VIEW_TASK_CONFIG['deal-pipeline']
  })

  // Column header configuration
  const columnHeaderConfig: ColumnHeaderConfig<DealKanbanItem> = useMemo(
    () => ({
      renderCount: (count: number) => (
        <span className='text-xs font-medium' style={{ color: '#6b7280' }}>
          {count} deals
        </span>
      ),
      renderTotal: (total: string | number, column) => {
        // Calculate total for this column
        const columnDeals = sortedItems.filter((item) => item.columnId === column.id)
        let columnTotal = 0
        let hasValidAmounts = false
        let primaryCurrency: string | null = null

        columnDeals.forEach((deal) => {
          const commitmentAmount = deal[DealFields.prog__commitment_amount] as number | null | undefined
          if (commitmentAmount && typeof commitmentAmount === 'number') {
            columnTotal += commitmentAmount
            hasValidAmounts = true
            if (!primaryCurrency) primaryCurrency = (deal[DealFields.deals__currency] as string | null) || null
          }
        })

        if (!hasValidAmounts) return null

        const formattedAmount = formatAmountById(columnTotal, primaryCurrency)
        const amountWithoutSymbol = formattedAmount
          .replace(/^[$€£¥₹₽¢₩₪₨₦₡₴₸₵₲₱₫₭₮₯₰₱₲₳₴₵₶₷₸₹₺₻₼₽₾₿＄￠￡￢￣￤￥￦]/g, '')
          .trim()

        return <span className='text-xs'>{amountWithoutSymbol} committed</span>
      }
    }),
    [sortedItems, formatAmountById]
  )

  const loading = dealsLoading

  return (
    <div className='h-full'>
      {commitGateHolder}
      <div className='w-full h-full flex flex-col bg-white pl-2 pr-4 pb-4'>
        {/* Small spacer between navbar and toolbar */}
        <div className='h-1 bg-white'></div>

        <ViewToolbar
          moduleKey={TABLE_DEALS}
          subModuleKey={TABLE_PIPELINE_DEALS}
          recordTitleField={DealFields.deals__name}
          onRefreshData={() => handleRefreshPipeline()}
        />

        {/* Kanban Board */}
        <KanbanBoard
          columns={columns}
          items={sortedItems}
          cardConfig={cardConfig}
          columnHeaderConfig={columnHeaderConfig}
          onMoveItem={handleMoveDeal}
          onUpdateItem={handleDealUpdate}
          loading={loading}
          emptyState={{
            title: 'No pipeline stages found',
            action: (
              <Button variant='primary' onClick={() => handleRefreshPipeline()}>
                Reload Pipeline
              </Button>
            )
          }}
          getColumnId={(item) => item.columnId}
          successfullyMovedCardId={successfullyMovedCardId}
        />
      </div>
    </div>
  )
}

export default EarlyStageKanban
